import { useEffect, useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export const ChangeAddressDialog = ({ order, open, onOpenChange, onChanged }) => {
  const [departments, setDepartments] = useState([]);
  const [address, setAddress] = useState(null);
  const [fee, setFee] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAddress(null); setFee(null);
    api.get("/delivery/config").then((r) => setDepartments(r.data.departments)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!address) { setFee(null); return; }
    let stale = false;
    api.get("/delivery/fee", { params: { postcode: address.postcode, city: address.city } })
      .then((r) => { if (!stale) setFee(r.data.fee); }).catch(() => {});
    return () => { stale = true; };
  }, [address]);

  const oldFee = order.delivery_fee || 0;
  const extra = fee === null ? 0 : Math.round((fee - oldFee) * 100) / 100;
  const isCard = order.payment_method === "card";

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/address`, {
        delivery_address: address.label, delivery_postcode: address.postcode, delivery_city: address.city,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success("Adresse modifiée — votre livreur est prévenu");
      onOpenChange(false); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-address-dialog" className="bg-[#0F1320] border-amber-500/20 text-slate-100">
        <DialogHeader>
          <DialogTitle className="text-slate-100">Changer d'adresse</DialogTitle>
          <DialogDescription className="text-slate-400">Adresse actuelle : {order.delivery_address}</DialogDescription>
        </DialogHeader>
        <AddressAutocomplete departments={departments} selected={address} onSelect={setAddress} />
        {address && fee !== null && (
          <div data-testid="change-address-summary" className="rounded-xl bg-[#161926] border border-amber-500/15 p-3 text-sm space-y-1">
            <p className="flex justify-between"><span className="text-slate-400">Frais de livraison</span><span className="font-mono">{fee > 0 ? `${fee.toFixed(2)} €` : "Offerts"}</span></p>
            {!isCard && <p className="flex justify-between"><span className="text-slate-400">Nouveau total à payer</span><span className="font-mono gold-text font-bold">{(order.subtotal + fee).toFixed(2)} €</span></p>}
            {isCard && extra > 0 && <p data-testid="change-address-extra" className="text-amber-200 text-xs">Supplément de {extra.toFixed(2)} € à payer par carte. L'adresse sera modifiée après le paiement.</p>}
            {isCard && extra <= 0 && <p className="text-slate-400 text-xs">Aucun supplément : le prix payé reste le même.</p>}
          </div>
        )}
        <Button data-testid="change-address-submit" disabled={!address || fee === null || busy} onClick={submit} className="w-full btn-gold rounded-xl py-6 text-base">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><MapPin className="w-4 h-4 mr-2" />{isCard && extra > 0 ? `Payer ${extra.toFixed(2)} € et changer` : "Confirmer la nouvelle adresse"}</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
